import { useCallback } from 'react';
import {
	BigNum,
	BN,
	BASE_PRECISION_EXP,
	MAX_LEVERAGE_ORDER_SIZE,
	MarketType,
	OptionalOrderParams,
	OrderTriggerCondition,
	PositionDirection,
	getTriggerLimitOrderParams,
	getTriggerMarketOrderParams,
} from '@drift-labs/sdk';
import {
	createEditOrderIx,
	createMultipleOpenPerpNonMarketOrderIx,
	MarketId,
	UIMarket,
	NonMarketOrderParamsConfig,
} from '@drift/common';
import { TransactionInstruction } from '@solana/web3.js';
import { BracketOrders } from 'src/components/TradeForm/TpSlOrderForm/types';
import useDriftClient from 'src/hooks/useDriftClient';
import { useGetTxParams } from 'src/hooks/driftActions/transactions/useGetTxParams';
import { useTxErrorHandler } from 'src/hooks/driftActions/transactions/useTxErrorHandler';
import { notify } from 'src/utils/notifications';
import useCurrentUserAccount from 'src/hooks/useCurrentUserAccount';
import { getUserMaxLeverageForMarket } from 'src/utils/position';
import invariant from 'tiny-invariant';
import { UiEnhancedIWalletV2 } from 'src/utils/wallets/IWalletV2';
import { useAutoSignDriftActionWithFallback } from 'src/hooks/wallets/useAutoSignDriftActionWithFallback';
import { FeePayerError } from 'src/constants/errors/FeePayerError';

interface ExistingOrderIdsToEdit {
	takeProfit?: number;
	stopLoss?: number;
}

interface OpenTpSlPerpOrdersParams {
	bracketOrders: BracketOrders;
	targetMarketIndex: number | undefined;
	baseAssetAmount: BigNum;
	existingOrderIdsToEdit?: ExistingOrderIdsToEdit;
}

/**
 * Hook that opens or edit existing take profit and stop loss orders for a perp position.
 */
export const useOpenTpSlPerpOrders = () => {
	const driftClient = useDriftClient();
	const currentUserAccount = useCurrentUserAccount();

	const getTxParams = useGetTxParams();
	const handleTxError = useTxErrorHandler();

	const openTpSlPerpOrders = useAutoSignDriftActionWithFallback(
		useCallback(
			async (
				signerWallet: UiEnhancedIWalletV2,
				isAutoSigned: boolean,
				toastId: string,
				{
					bracketOrders,
					targetMarketIndex,
					baseAssetAmount,
					existingOrderIdsToEdit: existingOrderIds,
				}: OpenTpSlPerpOrdersParams
			) => {
				const user = currentUserAccount?.client;

				invariant(driftClient, 'driftClient is required');
				invariant(user, 'user is required');
				invariant(
					targetMarketIndex !== undefined,
					'targetMarketIndex is required'
				);

				const getFormattedBaseAssetAmount = (amount: BigNum | BN): BN => {
					const amountBN = amount instanceof BigNum ? amount.val : amount;

					if (amountBN.eq(new BN(MAX_LEVERAGE_ORDER_SIZE))) {
						return amountBN;
					}

					return amount instanceof BigNum
						? amount.shiftTo(BASE_PRECISION_EXP).val
						: amountBN;
				};

				try {
					const bracketOrdersParams: {
						takeProfit?: OptionalOrderParams;
						stopLoss?: OptionalOrderParams;
					} = {};

					const baseAssetAmountForOrder =
						getFormattedBaseAssetAmount(baseAssetAmount);

					if (bracketOrders.takeProfit) {
						const tpBaseAssetAmount = bracketOrders.takeProfit.size
							? getFormattedBaseAssetAmount(bracketOrders.takeProfit.size)
							: baseAssetAmountForOrder;
						const isLimitOrder =
							bracketOrders.takeProfit.orderType === 'limit' &&
							!!bracketOrders.takeProfit.limitPrice?.val;

						const commonTakeProfitParams = {
							marketIndex: targetMarketIndex,
							marketType: MarketType.PERP,
							direction: bracketOrders.takeProfit.direction,
							baseAssetAmount: tpBaseAssetAmount,
							triggerPrice: bracketOrders.takeProfit.price?.val as BN,
							triggerCondition:
								bracketOrders.takeProfit.direction === PositionDirection.LONG
									? OrderTriggerCondition.BELOW
									: OrderTriggerCondition.ABOVE,
							reduceOnly: true,
						};

						bracketOrdersParams.takeProfit = isLimitOrder
							? getTriggerLimitOrderParams({
									...commonTakeProfitParams,
									price: bracketOrders.takeProfit.limitPrice?.val as BN,
								})
							: getTriggerMarketOrderParams(commonTakeProfitParams);
					}

					if (bracketOrders.stopLoss) {
						const slBaseAssetAmount = bracketOrders.stopLoss.size
							? getFormattedBaseAssetAmount(bracketOrders.stopLoss.size)
							: baseAssetAmountForOrder;
						const isLimitOrder =
							bracketOrders.stopLoss.orderType === 'limit' &&
							!!bracketOrders.stopLoss.limitPrice?.val;

						const commonStopLossParams = {
							marketIndex: targetMarketIndex,
							marketType: MarketType.PERP,
							direction: bracketOrders.stopLoss.direction,
							baseAssetAmount: slBaseAssetAmount,
							triggerPrice: bracketOrders.stopLoss.price?.val as BN,
							triggerCondition:
								bracketOrders.stopLoss.direction === PositionDirection.LONG
									? OrderTriggerCondition.ABOVE
									: OrderTriggerCondition.BELOW,
							reduceOnly: true,
						};

						bracketOrdersParams.stopLoss = isLimitOrder
							? getTriggerLimitOrderParams({
									...commonStopLossParams,
									price: bracketOrders.stopLoss.limitPrice?.val as BN,
								})
							: getTriggerMarketOrderParams(commonStopLossParams);
					}

					if (
						!bracketOrdersParams.takeProfit &&
						!bracketOrdersParams.stopLoss
					) {
						return undefined;
					}

					const ordersIxs: TransactionInstruction[] = [];
					const bracketOrdersParamsArray: NonMarketOrderParamsConfig[] = [];
					const positionMaxLeverage = getUserMaxLeverageForMarket(
						targetMarketIndex,
						driftClient,
						user,
						UIMarket.createPerpMarket(targetMarketIndex)
					);

					if (bracketOrdersParams.takeProfit) {
						if (existingOrderIds?.takeProfit) {
							const isLimitOrder =
								bracketOrders.takeProfit?.orderType === 'limit' &&
								!!bracketOrders.takeProfit?.limitPrice?.val;

							const editOrderIx = await createEditOrderIx({
								driftClient,
								user,
								orderId: existingOrderIds.takeProfit,
								editOrderParams: {
									newBaseAmount: bracketOrdersParams.takeProfit.baseAssetAmount,
									newTriggerPrice:
										bracketOrdersParams.takeProfit.triggerPrice || undefined,
									newLimitPrice: isLimitOrder
										? bracketOrdersParams.takeProfit.price || undefined
										: undefined,
								},
								mainSignerOverride: signerWallet.publicKey,
							});

							ordersIxs.push(editOrderIx);
						} else {
							bracketOrdersParamsArray.push({
								...bracketOrdersParams.takeProfit,
								marketType: MarketType.PERP,
								positionMaxLeverage,
								orderConfig: {
									orderType: 'takeProfit',
									triggerPrice: bracketOrdersParams.takeProfit
										.triggerPrice as BN,
									limitPrice: bracketOrdersParams.takeProfit.price,
								},
							});
						}
					}

					if (bracketOrdersParams.stopLoss) {
						if (existingOrderIds?.stopLoss) {
							const isLimitOrder =
								bracketOrders.stopLoss?.orderType === 'limit' &&
								!!bracketOrders.stopLoss?.limitPrice?.val;
							const editOrderIx = await createEditOrderIx({
								driftClient,
								user,
								orderId: existingOrderIds.stopLoss,
								editOrderParams: {
									newBaseAmount: bracketOrdersParams.stopLoss.baseAssetAmount,
									newTriggerPrice:
										bracketOrdersParams.stopLoss.triggerPrice || undefined,
									newLimitPrice: isLimitOrder
										? bracketOrdersParams.stopLoss.price || undefined
										: undefined,
								},
								mainSignerOverride: signerWallet.publicKey,
							});
							ordersIxs.push(editOrderIx);
						} else {
							bracketOrdersParamsArray.push({
								...bracketOrdersParams.stopLoss,
								marketType: MarketType.PERP,
								positionMaxLeverage,
								orderConfig: {
									orderType: 'stopLoss',
									triggerPrice: bracketOrdersParams.stopLoss.triggerPrice as BN,
									limitPrice: bracketOrdersParams.stopLoss.price,
								},
							});
						}
					}

					const isPlacingNewOrder = bracketOrdersParamsArray.length > 0;

					if (isPlacingNewOrder) {
						const ordersIx = await createMultipleOpenPerpNonMarketOrderIx({
							driftClient,
							user,
							orderParamsConfigs: bracketOrdersParamsArray,
							mainSignerOverride: signerWallet.publicKey,
						});
						ordersIxs.push(ordersIx);
					}

					if (ordersIxs.length === 0) {
						throw new Error('Failed to create TP/SL orders transaction');
					}

					const ordersType =
						bracketOrders.takeProfit && bracketOrders.stopLoss
							? 'Take Profit / Stop Loss'
							: bracketOrders.takeProfit
								? 'Take Profit'
								: 'Stop Loss';

					const orderStringEnd =
						bracketOrders.takeProfit && bracketOrders.stopLoss
							? ' orders'
							: ' order';

					const isEditingOrder =
						existingOrderIds?.takeProfit || existingOrderIds?.stopLoss;

					const orderStringSuffix = isEditingOrder ? ' edited' : ' placed';

					const preTxOrderPrefix =
						isEditingOrder && isPlacingNewOrder
							? 'Placing/editing'
							: isEditingOrder
								? 'Editing'
								: 'Placing';

					notify({
						type: isAutoSigned ? 'awaiting' : 'awaiting_wallet_confirmation',
						message: isAutoSigned
							? `${preTxOrderPrefix}${orderStringEnd}...`
							: undefined,
						id: toastId,
						updatePrevious: true,
						showUntilCancelled: true,
					});

					const txSig = await signerWallet.signAndSendTransaction(
						ordersIxs,
						getTxParams({
							marketId: MarketId.createPerpMarket(targetMarketIndex),
						})
					);

					notify({
						type: 'success',
						message: `${ordersType}${orderStringEnd}${orderStringSuffix}.`,
						description: '',
						id: toastId,
						updatePrevious: true,
					});

					return txSig;
				} catch (error: unknown) {
					console.error('Error in useOpenTpSlPerpOrders:', error);

					if (error instanceof FeePayerError) {
						throw error;
					}

					handleTxError(error, toastId);
				}
			},
			[driftClient, currentUserAccount, getTxParams, handleTxError]
		)
	);

	return openTpSlPerpOrders;
};
